/**
 * Niveaus Management Page
 *
 * HR and Admin page for managing function levels (niveaus) and their criteria.
 */

import { useState } from 'react';
import { Layout } from '../../components/Layout';
import { useNiveausWithStats, useNiveauSummary, useUpdateNiveau } from './useNiveaus';
import type { NiveauWithStats, UpdateNiveauInput } from '../../lib/niveausApi';

export function NiveausPage() {
  const { data: niveaus, isLoading, error } = useNiveausWithStats();
  const { data: summary } = useNiveauSummary();
  const updateNiveau = useUpdateNiveau();

  const [editingNiveau, setEditingNiveau] = useState<NiveauWithStats | null>(null);
  const [formData, setFormData] = useState<UpdateNiveauInput>({});

  function openEditModal(niveau: NiveauWithStats) {
    setFormData({
      name: niveau.name,
      beschrijving: niveau.beschrijving,
      criteria: niveau.criteria,
    });
    setEditingNiveau(niveau);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingNiveau) return;

    try {
      await updateNiveau.mutateAsync({ id: editingNiveau.id, input: formData });
      setEditingNiveau(null);
    } catch {
      // Error handled by mutation state
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Niveaus laden...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">Fout bij het laden van niveaus.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="niveaus-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Functieniveaus</h1>
          <p className="mt-1 text-sm text-gray-500">
            Beheer de 6 functieniveaus en hun criteria
          </p>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Totaal Medewerkers</div>
              <div className="text-2xl font-bold text-gray-900">{summary.totalEmployees}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-sm font-medium text-gray-500">Totaal Taken</div>
              <div className="text-2xl font-bold text-gray-900">{summary.totalTasks}</div>
            </div>
          </div>
        )}

        {/* Distribution Chart */}
        {summary && summary.distribution.length > 0 && (
          <div className="mb-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Verdeling</h2>
            <div className="space-y-3">
              {summary.distribution.map((item) => (
                <div key={item.niveauId} className="flex items-center">
                  <span className="w-24 text-sm font-medium text-gray-700">{item.niveauName}</span>
                  <div className="flex-1 mx-4">
                    <div className="flex h-6 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="bg-blue-500 flex items-center justify-center text-xs text-white"
                        style={{ width: `${item.employeesPercentage}%` }}
                        title={`${item.employees} medewerkers`}
                      >
                        {item.employeesPercentage > 10 && `${item.employees}`}
                      </div>
                      <div
                        className="bg-green-500 flex items-center justify-center text-xs text-white"
                        style={{ width: `${item.tasksPercentage}%` }}
                        title={`${item.tasks} taken`}
                      >
                        {item.tasksPercentage > 10 && `${item.tasks}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 w-32 text-right">
                    {item.employees} mw / {item.tasks} taken
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                <span className="text-gray-600">Medewerkers</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                <span className="text-gray-600">Taken</span>
              </div>
            </div>
          </div>
        )}

        {/* Niveaus List */}
        <div className="space-y-4">
          {niveaus?.map((niveau) => (
            <div
              key={niveau.id}
              className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">{niveau.id}</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">{niveau.name}</h3>
                      {niveau.beschrijving && (
                        <p className="text-sm text-gray-500">{niveau.beschrijving}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => openEditModal(niveau)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Bewerken
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex items-center text-sm">
                    <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-gray-600">{niveau.employeesCount} medewerkers</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="text-gray-600">{niveau.tasksCount} taken</span>
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Criteria</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{niveau.criteria}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit Modal */}
        {editingNiveau && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <form onSubmit={handleSubmit}>
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-600">{editingNiveau.id}</span>
                      </div>
                      <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
                        Niveau Bewerken
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Naam
                        </label>
                        <input
                          type="text"
                          value={formData.name ?? ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Beschrijving
                        </label>
                        <input
                          type="text"
                          value={formData.beschrijving ?? ''}
                          onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value || null })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Criteria *
                        </label>
                        <textarea
                          value={formData.criteria ?? ''}
                          onChange={(e) => setFormData({ ...formData, criteria: e.target.value })}
                          rows={8}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Beschrijf de verwachtingen en criteria voor dit niveau..."
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Beschrijf wat er van medewerkers op dit niveau verwacht wordt.
                        </p>
                      </div>
                    </div>
                  </div>

                  {updateNiveau.error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">
                        Er is een fout opgetreden. Probeer het opnieuw.
                      </p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={updateNiveau.isPending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {updateNiveau.isPending ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingNiveau(null)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Annuleren
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
